import {
  ApplicationConfig,
  importProvidersFrom,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withXsrfConfiguration,
} from '@angular/common/http';
import { provideClientHydration } from '@angular/platform-browser';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { provideUiTour } from 'ngx-ui-tour-md-menu';
import { BayerInterceptor } from './core/interceptor/bayer.interceptor';
import { DatePipe } from '@angular/common';
import { environment } from '@environments/environment';

declare global {
  function OptanonWrapper(): void;
}
export const GTMConfig = {
  gtmId: 'GTM-P9CVJ4W7',
};

function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/translate/');
}

export const provideTranslation = () => ({
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpClient],
  },
});

export function loadCookieScripts() {
  const script1 = document.createElement('script');
  script1.src = `https://cdn.cookielaw.org/consent/${environment.cookieId}/OtAutoBlock.js`;
  script1.type = 'text/javascript';
  script1.onload = () => {
    const script2 = document.createElement('script');
    script2.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
    script2.type = 'text/javascript';
    script2.dataset['domainScript'] = environment.cookieId;
    script2.dataset['documentLanguage'] = 'true';
    script2.onload = () => {
      if (typeof OptanonWrapper === 'function') {
        OptanonWrapper();
      }
    };
    document.head.appendChild(script2);
  };
  document.head.appendChild(script1);
}

export function loadGTMScript() {
  if (document.getElementById('gtm-script')) return;

  globalThis.dataLayer = globalThis.dataLayer ?? [];
  const now = new Date();
  globalThis.dataLayer.push({
    'gtm.start': now.getTime(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.async = true;
  script.id = 'gtm-script';
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTMConfig.gtmId}`;
  const head = document.getElementsByTagName('head')[0];
  if (head.firstChild) {
    head.insertBefore(script, head.firstChild);
  } else {
    head.appendChild(script);
  }
}

export function loadScript() {
  return () =>
    new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = `https://cdns.gigya.com/JS/gigya.js?apiKey=${environment.apiKey}`;
      script.onload = () => resolve(true);
      document.body.appendChild(script);
    });
}

export function loadPowerBIScript() {
  return () =>
    new Promise((resolve) => {
      const powerBiScript = document.createElement('script');
      powerBiScript.src =
        'https://cdnjs.cloudflare.com/ajax/libs/powerbi-client/2.23.7/powerbi.js';
      powerBiScript.onload = () => resolve(true);

      document.body.appendChild(powerBiScript);
    });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    importProvidersFrom(TranslateModule.forRoot(provideTranslation())),

    provideRouter(routes),
    provideClientHydration(),
    provideNativeDateAdapter(),
    provideAngularSvgIcon(),
    provideUiTour(),

    provideHttpClient(
      withXsrfConfiguration({
        cookieName: '_csrf_token',
        headerName: 'X-CSRF-TOKEN',
      }),
      withInterceptorsFromDi(),
    ),
    { provide: HTTP_INTERCEPTORS, useClass: BayerInterceptor, multi: true },

    provideAppInitializer(loadScript()),
    provideAppInitializer(loadPowerBIScript()),
    DatePipe,
  ],
};
